import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Checkbox, Chip, Collapse, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, Grid, IconButton,
  InputLabel, MenuItem, Paper, Radio, Select, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField,
  Tooltip, Typography, CircularProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon, LocalShipping as ShippingIcon,
  ShoppingCart as CartIcon, Print as PrintIcon, CheckCircleOutline as PrintedIcon,
  OpenInNew as OpenInNewIcon, ContentCopy as CopyIcon,
  Undo as RefundIcon, Download as DownloadIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import i18n, { intlLocale } from '../i18n/i18n';
import { translateServerMessage } from '../i18n/serverMessages';

const ORDER_STATUSES = ['PENDING', 'PAID', 'PROCESSING', 'SHIPPED', 'DELIVERED', 'CANCELLED', 'REFUNDED'];

const STATUS_COLORS = {
  PENDING: 'default',
  PAID: 'info',
  PROCESSING: 'warning',
  SHIPPED: 'secondary',
  DELIVERED: 'success',
  CANCELLED: 'error',
  REFUNDED: 'default',
};

const STATUS_SX = {
  PAID: { bgcolor: '#1976d2', color: '#fff' },
  PROCESSING: { bgcolor: '#ed6c02', color: '#fff' },
  SHIPPED: { bgcolor: '#9c27b0', color: '#fff' },
  DELIVERED: { bgcolor: '#2e7d32', color: '#fff' },
  CANCELLED: { bgcolor: '#d32f2f', color: '#fff' },
  REFUNDED: { bgcolor: '#757575', color: '#fff' },
  PENDING: { bgcolor: '#e0e0e0', color: '#424242' },
};

// Order source (origin): PURCHASE = individual one-off order, SUBSCRIPTION = created by a
// subscription billing cycle. Lets admin/fulfillment tell them apart and filter. The labels are
// `source.<value>` in the bookOrders namespace.
const SOURCE_SX = {
  SUBSCRIPTION: { bgcolor: '#6a1b9a', color: '#fff' },
  PURCHASE: { bgcolor: '#e0e0e0', color: '#424242' },
};

// DV-288: the buckets staff asked for. Values are the backend's book-count range; "3+" has no
// upper bound. Counts are books, not order lines — two sets of a one-book bundle is two books.
// DV-292: the three parts of an order that can be refunded separately. They cover it exactly —
// total = subtotal + shipping + tax — so ticking all three is a full refund.
// Labels are `refund.components.<key>`.
const REFUND_COMPONENTS = [
  { key: 'BOOKS', amountOf: (o) => o?.subtotal },
  { key: 'SHIPPING', amountOf: (o) => o?.shippingCost },
  { key: 'TAX', amountOf: (o) => o?.taxAmount },
];

const BOOK_COUNT_BUCKETS = {
  ALL: {},
  ONE: { minBooks: 1, maxBooks: 1 },
  TWO: { minBooks: 2, maxBooks: 2 },
  THREE_PLUS: { minBooks: 3 },
};
// Labels are `filters.bookCount.<key>`.

// For the data-loading callbacks, so a language switch doesn't refetch the orders.
const tOrders = (key, options) => i18n.t(key, { ns: 'bookOrders', ...options });

const BookOrderManagementPage = () => {
  const { t } = useTranslation('bookOrders');
  const { getAccessToken } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalElements, setTotalElements] = useState(0);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sourceFilter, setSourceFilter] = useState('ALL');
  const [teamFilter, setTeamFilter] = useState([]);
  const [booksFilter, setBooksFilter] = useState('ALL');
  // The subscription period a set of books belongs to, which is the question fulfilment actually
  // asks: "what is going out for this month?". Not the same as created-in-that-month — a period's
  // books can be raised late, and a deferred set ships after the period that paid for it.
  const [periodFilter, setPeriodFilter] = useState('ALL');
  const [teams, setTeams] = useState([]);
  // Typed filters are held twice: what is on screen, and what has been sent. Firing a query per
  // keystroke would put the grid a few characters behind whatever the user is reading.
  const [searchInput, setSearchInput] = useState('');
  const [titleInput, setTitleInput] = useState('');
  const [search, setSearch] = useState('');
  const [title, setTitle] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);

  // Status update dialog
  const [statusDialog, setStatusDialog] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkTrackingNumber, setBulkTrackingNumber] = useState('');
  const [bulkCarrier, setBulkCarrier] = useState('');

  // Refund dialog
  const [refundDialog, setRefundDialog] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundParts, setRefundParts] = useState([]);

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) throw new Error(tOrders('errors.authRequired'));
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getAccessToken]);

  // This month first, then the months behind it. Far enough back to cover a deferred set still
  // working its way out, short enough to stay a glance rather than a scroll.
  const periodOptions = React.useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 6; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return out;
  }, []);

  // Shared by the list and the CSV export, so the download is always exactly what is on screen.
  const filterBody = React.useMemo(() => ({
    ...(statusFilter !== 'ALL' ? { statuses: [statusFilter] } : {}),
    ...(sourceFilter !== 'ALL' ? { sources: [sourceFilter] } : {}),
    ...(teamFilter.length ? { teamNames: teamFilter } : {}),
    ...(search ? { search } : {}),
    ...(title ? { bookTitle: title } : {}),
    ...BOOK_COUNT_BUCKETS[booksFilter],
    ...(periodFilter !== 'ALL' ? { periodKey: periodFilter } : {}),
  }), [statusFilter, sourceFilter, teamFilter, booksFilter, periodFilter, search, title]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const body = { page, size: rowsPerPage, ...filterBody };

      const res = await fetch(
        `${config.API_BASE_URL}/api/v1/content/admin/book-orders/search`,
        { method: 'POST', headers, body: JSON.stringify(body) },
      );
      if (!res.ok) throw new Error(tOrders('errors.fetchOrders'));
      const data = await res.json();

      if (data.content) {
        setOrders(data.content);
        setTotalElements(data.totalElements ?? data.content.length);
      } else {
        const arr = Array.isArray(data) ? data : data.data || [];
        setOrders(arr);
        setTotalElements(arr.length);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, page, rowsPerPage, filterBody]);

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${config.API_BASE_URL}/api/v1/content/admin/book-orders/export`,
        { method: 'POST', headers, body: JSON.stringify(filterBody) },
      );
      if (!res.ok) throw new Error(t('errors.exportFailedStatus', { status: res.status }));
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const scope = periodFilter !== 'ALL' ? periodFilter : new Date().toISOString().slice(0, 10);
      // The file name stays in English: it is a file staff keep and other tools may look for.
      a.download = `book-orders-${scope}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || t('errors.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Settle typing before searching, and go back to page 1 — a filtered result set is shorter,
  // so staying on page 4 would usually land on nothing.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setTitle(titleInput.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput, titleInput]);

  // Every team on record. A team with no orders simply returns an empty list, which is a
  // clearer answer than hiding the team.
  useEffect(() => {
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(
          `${config.API_BASE_URL}/api/v1/content/admin/book-orders/teams`, { headers },
        );
        if (res.ok) setTeams(await res.json());
      } catch {
        // A missing team list only costs the dropdown; the rest of the page still works.
      }
    })();
  }, [authHeaders]);

  const handlePageChange = (_, p) => { setPage(p); setSelected(new Set()); };
  const handleRowsChange = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  const toggleExpand = (id) => setExpandedOrder((prev) => (prev === id ? null : id));

  // ─── Single status update ───
  const openStatusDialog = (order) => {
    setStatusTarget(order);
    setNewStatus(order.status || '');
    setTrackingNumber(order.trackingNumber || '');
    setCarrier(order.carrier || '');
    setStatusDialog(true);
  };

  const handleUpdateStatus = async () => {
    if (!statusTarget || !newStatus) return;
    try {
      const headers = await authHeaders();
      const body = { status: newStatus, trackingNumber: trackingNumber || null, carrier: carrier || null };
      const res = await fetch(
        `${config.API_BASE_URL}/api/v1/content/admin/book-orders/${statusTarget.id}/status`,
        { method: 'PUT', headers, body: JSON.stringify(body) },
      );
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(translateServerMessage(e.message, t('errors.updateFailed'))); }
      toast.success(t('toast.statusUpdated'));
      setStatusDialog(false);
      fetchOrders();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Bulk status update ───
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === orders.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(orders.map((o) => o.id)));
    }
  };

  const openBulkDialog = () => {
    if (selected.size === 0) { toast.warn(t('toast.selectOrder')); return; }
    setBulkStatus('');
    setBulkTrackingNumber('');
    setBulkCarrier('');
    setBulkDialog(true);
  };

  const handleBulkUpdate = async () => {
    if (!bulkStatus) { toast.error(t('toast.selectStatus')); return; }
    try {
      const headers = await authHeaders();
      const body = {
        orderIds: Array.from(selected),
        status: bulkStatus,
        trackingNumber: bulkTrackingNumber || null,
        carrier: bulkCarrier || null,
      };
      const res = await fetch(
        `${config.API_BASE_URL}/api/v1/content/admin/book-orders/bulk-status`,
        { method: 'PUT', headers, body: JSON.stringify(body) },
      );
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(translateServerMessage(e.message, t('errors.bulkUpdateFailed'))); }
      toast.success(t('toast.bulkUpdated', { count: selected.size }));
      setBulkDialog(false);
      setSelected(new Set());
      fetchOrders();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Label purchase (with shipping-option override, DV-249) ───
  const [labelLoading, setLabelLoading] = useState(null);
  const [labelDialog, setLabelDialog] = useState(null); // { order, option }
  const [rateChoice, setRateChoice] = useState({});      // { [orderId]: "carrier|service" }

  const optKey = (o) => `${o?.carrier || ''}|${o?.service || ''}`;

  // Options the admin can pick from: those for the order's own warehouse (the one that ships),
  // so the same carrier/service isn't listed once per warehouse.
  const optionsForOrder = (order) => {
    const all = Array.isArray(order.shippingOptions) ? order.shippingOptions : [];
    const chosen = all.find((o) => o.selected);
    const wh = chosen ? chosen.warehouseId : null;
    return wh != null ? all.filter((o) => o.warehouseId === wh) : all;
  };

  // The cheapest / checkout-chosen option's key (the default selection).
  const defaultOptionKey = (options) => {
    const chosen = options.find((o) => o.selected);
    if (chosen) return optKey(chosen);
    if (options.length === 0) return '';
    return optKey(options.reduce((a, b) => (Number(b.shippingCost) < Number(a.shippingCost) ? b : a)));
  };

  const selectedOptionFor = (order) => {
    const options = optionsForOrder(order);
    const key = rateChoice[order.id] || defaultOptionKey(options);
    return options.find((o) => optKey(o) === key) || null;
  };

  // Open the confirm using whatever option is selected in the order's Shipping Options table.
  const handlePurchaseLabel = (order) => {
    setLabelDialog({ order, option: selectedOptionFor(order) });
  };

  const confirmPurchaseLabel = async () => {
    if (!labelDialog) return;
    const { order, option } = labelDialog;
    setLabelLoading(order.id);
    try {
      const headers = await authHeaders();
      // Send the chosen carrier/service; omit the body to let the backend pick the cheapest.
      const body = option ? JSON.stringify({ carrier: option.carrier, service: option.service }) : undefined;
      const res = await fetch(
        `${config.API_BASE_URL}/api/v1/content/admin/book-orders/${order.id}/purchase-label`,
        { method: 'POST', headers, body },
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(translateServerMessage(e.message, t('errors.labelPurchaseFailed')));
      }
      toast.success(t('toast.labelPurchased', { order: order.orderNumber }));
      setLabelDialog(null);
      fetchOrders();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLabelLoading(null);
    }
  };

  // ─── Bulk label purchase (DV-289) ───
  // The backend caps a run so the request finishes before the proxy's read timeout; mirrored here
  // so staff are told before they spend the click rather than after.
  const MAX_BULK_LABELS = 20;

  const [bulkLabelDialog, setBulkLabelDialog] = useState(false);
  const [bulkLabelLoading, setBulkLabelLoading] = useState(false);
  const [bulkLabelResult, setBulkLabelResult] = useState(null);

  const selectedOrders = orders.filter((o) => selected.has(o.id));
  // An order that already has a label would fail every time — leave it out rather than spend a
  // carrier round trip discovering that.
  const selectedNeedingLabel = selectedOrders.filter((o) => !o.labelUrl);
  const selectedAlreadyLabelled = selectedOrders.length - selectedNeedingLabel.length;

  const runBulkLabelPurchase = async () => {
    setBulkLabelLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${config.API_BASE_URL}/api/v1/content/admin/book-orders/bulk-purchase-labels`,
        { method: 'POST', headers, body: JSON.stringify({ orderIds: selectedNeedingLabel.map((o) => o.id) }) },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(translateServerMessage(data.message, t('errors.bulkLabelPurchaseFailed')));

      setBulkLabelDialog(false);
      setBulkLabelResult(data);
      if (data.purchased > 0) {
        toast.success(t('toast.labelsPurchased', { count: data.purchased }));
      }
      if (data.failed > 0) {
        toast.error(t('toast.labelsFailed', { count: data.failed }));
      }
      // Only the orders that succeeded should lose their tick, so a retry keeps the rest selected.
      const boughtIds = new Set((data.results || [])
        .filter((r) => r.status === 'PURCHASED').map((r) => r.orderId));
      setSelected((prev) => new Set([...prev].filter((id) => !boughtIds.has(id))));
      fetchOrders();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBulkLabelLoading(false);
    }
  };

  // ─── Bulk label print (DV-290) ───
  // Which orders are printing, not merely that something is. A single boolean put every row's
  // button into the loading state at once, so printing one label looked like printing all of them.
  const [printingIds, setPrintingIds] = useState(new Set());

  // Only orders that actually have a label can be printed. Filtering here means the count on the
  // button is the number of pages staff will get.
  const selectedWithLabel = selectedOrders.filter((o) => o.labelUrl);
  // Reprinting is allowed — a label can be spoiled — but it should never be accidental.
  const selectedAlreadyPrinted = selectedWithLabel.filter((o) => o.labelPrintedAt);
  const printingBulk = selectedWithLabel.some((o) => printingIds.has(o.id));

  // Every print goes through the same endpoint, one order or fifty. Opening the label file
  // directly used to leave the browser to size it, which is how a 4x6 label ended up filling a
  // sheet of paper; the endpoint returns pages that are already label-sized.
  const printLabels = async (orderIds) => {
    if (!orderIds.length) return;
    setPrintingIds((prev) => new Set([...prev, ...orderIds]));
    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${config.API_BASE_URL}/api/v1/content/admin/book-orders/bulk-print-labels`,
        { method: 'POST', headers, body: JSON.stringify({ orderIds }) },
      );
      if (!res.ok) {
        // The failure path is JSON even though the success path is a PDF.
        const e = await res.json().catch(() => ({}));
        throw new Error(translateServerMessage(e.message, t('errors.labelDocumentFailed')));
      }

      const skipped = res.headers.get('X-Labels-Skipped');
      const count = res.headers.get('X-Labels-Count');
      const blob = await res.blob();
      // Opened rather than downloaded: staff print straight from the viewer, and a tab they can
      // close beats a file they have to find in Downloads and delete later.
      const url = URL.createObjectURL(blob);
      const opened = window.open(url, '_blank');
      if (!opened) {
        toast.error(t('errors.allowPopups'));
      }
      // Revoking immediately would race the new tab's load on some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 60000);

      toast.success(t('toast.labelsReady', { count: Number(count || orderIds.length) }));
      if (skipped) {
        toast.error(t('toast.leftOut', { orders: skipped }), { autoClose: false });
      }
      // The server records who printed what. Re-reading it keeps the button honest even when
      // someone else on another machine printed the same run a minute ago.
      fetchOrders();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPrintingIds((prev) => {
        const next = new Set(prev);
        orderIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  // ─── Refund ───
  const openRefundDialog = (order) => {
    setRefundTarget(order);
    setRefundReason('');
    // Default to whatever has not been refunded yet — the common case is refunding the rest.
    const done = order.refundedComponents || [];
    setRefundParts(REFUND_COMPONENTS
      .filter((c) => !done.includes(c.key) && Number(c.amountOf(order)) > 0)
      .map((c) => c.key));
    setRefundDialog(true);
  };

  const refundedAlready = refundTarget?.refundedComponents || [];

  const refundTotal = REFUND_COMPONENTS
    .filter((c) => refundParts.includes(c.key))
    .reduce((sum, c) => sum + Number(c.amountOf(refundTarget) || 0), 0);

  // Tax is charged on the books, so refunding the books without it leaves the member paying tax
  // on something they no longer have. The backend refuses this; surfacing it here means the admin
  // fixes the selection rather than meeting a rejection.
  const booksWithoutTax = refundParts.includes('BOOKS')
    && !refundParts.includes('TAX')
    && !refundedAlready.includes('TAX')
    && Number(refundTarget?.taxAmount) > 0;

  // Allowed — tax charged in error is a real correction — but rarely what someone means.
  const taxOnly = refundParts.length === 1 && refundParts[0] === 'TAX';

  // Refundable whenever money was taken and something is still outstanding. SHIPPED and DELIVERED
  // are included on purpose — that is exactly when a shipping refund is usually warranted — and a
  // partially refunded order stays refundable for the rest.
  const canRefund = (o) => Boolean(o.paymentId)
    && o.status !== 'REFUNDED'
    && o.status !== 'PENDING'
    && o.status !== 'PAYMENT_FAILED'
    && (o.refundedComponents || []).length < REFUND_COMPONENTS.length;

  const toggleRefundPart = (key) => setRefundParts((prev) => (
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  ));

  const handleRefund = async () => {
    if (!refundTarget) return;
    setRefundLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${config.API_BASE_URL}/api/v1/content/admin/book-orders/${refundTarget.id}/refund`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ reason: refundReason || null, components: refundParts }),
        },
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(translateServerMessage(e.message, t('errors.refundFailed')));
      }
      toast.success(t('toast.refunded', { amount: fmtCurrency(refundTotal), order: refundTarget.orderNumber }));
      setRefundDialog(false);
      fetchOrders();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRefundLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.info(t('toast.copied'));
  };

  // ─── Helpers ───
  const fmtCurrency = (v) => `$${parseFloat(v || 0).toFixed(2)}`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(intlLocale()) : '—';
  const itemSummary = (items) => {
    if (!items || items.length === 0) return '—';
    if (items.length === 1) return items[0].itemTitle || items[0].bookTitle || items[0].title || t('items', { count: 1 });
    return t('items', { count: items.length });
  };
  // The raw values stay for filters, colours and API calls; only what is shown is translated.
  const statusLabel = (s) => (s ? t(`status.${s}`, { defaultValue: s }) : s);
  const sourceLabel = (s) => t(`source.${s}`, { defaultValue: s });
  const bulkResultLabel = (s) => t(`bulkResult.status.${s}`, { defaultValue: (s || '').toLowerCase() });

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>{t('title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('subtitle')}</Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Tooltip title={t('export.tooltip')}>
            <span>
              <Button variant="outlined" size="small" onClick={handleExport}
                disabled={exporting || totalElements === 0}
                startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}>
                {exporting ? t('export.preparing') : t('export.button')}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={t('refresh')}>
            <IconButton onClick={fetchOrders} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters & Bulk Action Bar */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('filters.status')}</InputLabel>
              <Select value={statusFilter} label={t('filters.status')}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                <MenuItem value="ALL">{t('filters.allStatuses')}</MenuItem>
                {ORDER_STATUSES.map((s) => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('filters.source')}</InputLabel>
              <Select value={sourceFilter} label={t('filters.source')}
                onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }}>
                <MenuItem value="ALL">{t('filters.allSources')}</MenuItem>
                <MenuItem value="PURCHASE">{sourceLabel('PURCHASE')}</MenuItem>
                <MenuItem value="SUBSCRIPTION">{sourceLabel('SUBSCRIPTION')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('filters.period')}</InputLabel>
              <Select value={periodFilter} label={t('filters.period')}
                onChange={(e) => { setPeriodFilter(e.target.value); setPage(0); }}>
                <MenuItem value="ALL">{t('filters.allPeriods')}</MenuItem>
                {periodOptions.map((p, i) => (
                  <MenuItem key={p} value={p}>{i === 0 ? t('filters.thisMonth', { period: p }) : p}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              {/* A multi-select holding an empty array counts as empty, so MUI skips renderValue
                  and never notches the label — the control collapses to a blank box. displayEmpty
                  with a shrunk, notched label keeps it looking like the selects either side of it. */}
              <InputLabel id="team-filter-label" shrink>{t('filters.team')}</InputLabel>
              <Select
                labelId="team-filter-label"
                multiple
                displayEmpty
                notched
                value={teamFilter}
                label={t('filters.team')}
                onChange={(e) => { setTeamFilter(e.target.value); setPage(0); }}
                renderValue={(v) => {
                  if (v.length === 0) {
                    return <Typography variant="body2" color="text.secondary">{t('filters.allTeams')}</Typography>;
                  }
                  return v.length === 1 ? v[0] : t('filters.teamCount', { count: v.length });
                }}
              >
                {teams.length === 0 && <MenuItem disabled value="">{t('filters.noTeams')}</MenuItem>}
                {teams.map((team) => (
                  <MenuItem key={team} value={team}>
                    <Checkbox size="small" checked={teamFilter.indexOf(team) > -1} />
                    {team}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('filters.booksPerOrder')}</InputLabel>
              <Select value={booksFilter} label={t('filters.booksPerOrder')}
                onChange={(e) => { setBooksFilter(e.target.value); setPage(0); }}>
                {Object.keys(BOOK_COUNT_BUCKETS).map((k) => (
                  <MenuItem key={k} value={k}>{t(`filters.bookCount.${k}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth size="small" label={t('filters.search')}
              placeholder={t('filters.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth size="small" label={t('filters.title')}
              placeholder={t('filters.titlePlaceholder')}
              value={titleInput}
              onChange={(e) => setTitleInput(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            {selected.size > 0 && (
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button variant="outlined" size="small" onClick={openBulkDialog}
                  sx={{ textTransform: 'none', fontWeight: 600 }}>
                  {t('bulk.update', { count: selected.size })}
                </Button>
                {selectedNeedingLabel.length > 0 && (
                  <Button variant="contained" size="small" color="secondary"
                    startIcon={<PrintIcon />}
                    onClick={() => setBulkLabelDialog(true)}
                    sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {t('bulk.buyLabels', { count: selectedNeedingLabel.length })}
                  </Button>
                )}
                {selectedWithLabel.length > 0 && (
                  <Tooltip title={selectedAlreadyPrinted.length
                    ? t('bulk.alreadyPrinted', { count: selectedAlreadyPrinted.length })
                    : ''}>
                    <Button variant="contained" size="small"
                      color={selectedAlreadyPrinted.length === selectedWithLabel.length
                        ? 'success' : 'primary'}
                      disabled={printingBulk}
                      startIcon={printingBulk ? <CircularProgress size={14} /> : <PrintIcon />}
                      onClick={() => printLabels(selectedWithLabel.map((o) => o.id))}
                      sx={{ textTransform: 'none', fontWeight: 600 }}>
                      {selectedAlreadyPrinted.length > 0
                        ? t('bulk.printLabelsWithReprint', {
                          count: selectedWithLabel.length, reprint: selectedAlreadyPrinted.length,
                        })
                        : t('bulk.printLabels', { count: selectedWithLabel.length })}
                    </Button>
                  </Tooltip>
                )}
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="body2" color="text.secondary" textAlign="right">
              {t('totalOrders', { count: totalElements })}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Orders Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selected.size > 0 && selected.size < orders.length}
                      checked={orders.length > 0 && selected.size === orders.length}
                      onChange={toggleSelectAll}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 40 }} />
                  <TableCell sx={{ fontWeight: 700 }}>{t('table.order')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('table.member')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('table.items')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>{t('table.books')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('table.subtotal')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('table.shipping')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('table.tax')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('table.total')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('table.status')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('table.source')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('table.created')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{t('table.action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center" sx={{ py: 6 }}>
                      <CartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">{t('table.empty')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((o) => {
                    const isExpanded = expandedOrder === o.id;
                    return (
                      <React.Fragment key={o.id}>
                        <TableRow hover sx={{ '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}>
                          <TableCell padding="checkbox">
                            <Checkbox checked={selected.has(o.id)} onChange={() => toggleSelect(o.id)} />
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => toggleExpand(o.id)}>
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{o.orderNumber || o.id}</TableCell>
                          {/* The name is what staff read; the IBO number is how they confirm it.
                              A database id identified nobody and is gone. */}
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {o.iboName || o.iboNumber || '—'}
                              </Typography>
                              {o.iboName && o.iboNumber && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {o.iboNumber}
                                </Typography>
                              )}
                              {o.teamName && (
                                <Typography variant="caption" color="text.secondary">{o.teamName}</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{itemSummary(o.items || o.orderItems)}</TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={600}>
                              {o.totalBooks ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>{fmtCurrency(o.subtotal)}</TableCell>
                          <TableCell>{fmtCurrency(o.shippingCost || o.shipping)}</TableCell>
                          <TableCell>{fmtCurrency(o.taxAmount || o.tax)}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{fmtCurrency(o.totalAmount || o.total)}</TableCell>
                          <TableCell>
                            <Chip label={statusLabel(o.status)} size="small"
                              sx={{ fontWeight: 600, ...(STATUS_SX[o.status] || {}) }} />
                          </TableCell>
                          <TableCell>
                            <Chip label={o.orderType ? sourceLabel(o.orderType) : '—'} size="small"
                              sx={{ fontWeight: 600, ...(SOURCE_SX[o.orderType] || {}) }} />
                          </TableCell>
                          <TableCell>{fmtDate(o.createdAt || o.orderDate)}</TableCell>
                          <TableCell align="right">
                            <Box display="flex" gap={0.5} justifyContent="flex-end" flexWrap="wrap">
                              {/* One slot, two states. The row used to show Buy Label and then
                                  nothing at all once the label existed, so printing meant
                                  expanding every order to reach a link two clicks down. */}
                              {o.labelUrl ? (
                                <Tooltip title={o.labelPrintedAt
                                  ? (o.labelPrintedBy
                                    ? t('row.printedByTooltip', { date: fmtDate(o.labelPrintedAt), by: o.labelPrintedBy })
                                    : t('row.printedTooltip', { date: fmtDate(o.labelPrintedAt) }))
                                  : t('row.printTooltip')}>
                                  <Button size="small"
                                    variant={o.labelPrintedAt ? 'outlined' : 'contained'}
                                    color={o.labelPrintedAt ? 'success' : 'primary'}
                                    disabled={printingIds.has(o.id)}
                                    onClick={() => printLabels([o.id])}
                                    startIcon={printingIds.has(o.id)
                                      ? <CircularProgress size={14} />
                                      : o.labelPrintedAt ? <PrintedIcon /> : <PrintIcon />}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                                    {o.labelPrintedAt ? t('row.printed') : t('row.printLabel')}
                                  </Button>
                                </Tooltip>
                              ) : o.easypostShipmentId ? (
                                <Tooltip title={t('row.buyLabelTooltip')}>
                                  <Button size="small" variant="contained" color="secondary"
                                    disabled={labelLoading === o.id}
                                    onClick={() => handlePurchaseLabel(o)}
                                    startIcon={labelLoading === o.id ? <CircularProgress size={14} /> : <PrintIcon />}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                                    {t('row.buyLabel')}
                                  </Button>
                                </Tooltip>
                              ) : null}
                              {canRefund(o) && (
                                <Tooltip title={t('row.refundTooltip')}>
                                  <Button size="small" variant="outlined" color="error"
                                    onClick={() => openRefundDialog(o)}
                                    startIcon={<RefundIcon />}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                                    {t('row.refund')}
                                  </Button>
                                </Tooltip>
                              )}
                              <Button size="small" variant="outlined"
                                onClick={() => openStatusDialog(o)}
                                sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                                {t('row.update')}
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>

                        {/* Expanded detail row */}
                        <TableRow>
                          <TableCell colSpan={14} sx={{ py: 0, px: 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 3, bgcolor: 'action.hover' }}>
                                <Grid container spacing={3}>
                                  {/* Items */}
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" fontWeight={700} mb={1}>{t('detail.orderItems')}</Typography>
                                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('detail.itemTitle')}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('detail.qty')}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('detail.price')}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('detail.total')}</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {(o.items || o.orderItems || []).map((item, idx) => (
                                            <TableRow key={idx}>
                                              <TableCell>{item.itemTitle || item.bookTitle || item.title || '—'}</TableCell>
                                              <TableCell>{item.quantity || 1}</TableCell>
                                              <TableCell>{fmtCurrency(item.price || item.unitPrice)}</TableCell>
                                              <TableCell>{fmtCurrency((item.price || item.unitPrice || 0) * (item.quantity || 1))}</TableCell>
                                            </TableRow>
                                          ))}
                                          {(!o.items && !o.orderItems) && (
                                            <TableRow>
                                              <TableCell colSpan={4} align="center">
                                                <Typography variant="body2" color="text.secondary">{t('detail.noItems')}</Typography>
                                              </TableCell>
                                            </TableRow>
                                          )}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>

                                    {/* Shipping Options — selectable until the label is bought (DV-249) */}
                                    {(() => {
                                      const options = optionsForOrder(o);
                                      if (options.length === 0) return null;
                                      const canPick = !o.labelUrl;
                                      const selectedKey = rateChoice[o.id] || defaultOptionKey(options);
                                      // After purchase, highlight the service actually bought (not the cheapest).
                                      const boughtKey = !canPick ? `${o.carrier || ''}|${o.shippingService || ''}` : null;
                                      return (
                                        <Box mt={2}>
                                          <Typography variant="subtitle2" fontWeight={700} mb={1}>{t('options.title')}</Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                            {canPick
                                              ? t('options.pickHint')
                                              : t('options.boughtHint')}
                                          </Typography>
                                          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow>
                                                  {canPick && <TableCell padding="checkbox" />}
                                                  <TableCell sx={{ fontWeight: 600 }}>{t('options.carrier')}</TableCell>
                                                  <TableCell sx={{ fontWeight: 600 }}>{t('options.service')}</TableCell>
                                                  <TableCell sx={{ fontWeight: 600 }}>{t('options.estDays')}</TableCell>
                                                  <TableCell sx={{ fontWeight: 600 }} align="right">{t('options.cost')}</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {options.map((opt, idx) => {
                                                  const isBought = boughtKey != null && optKey(opt) === boughtKey;
                                                  const isSel = canPick ? optKey(opt) === selectedKey : isBought;
                                                  const pick = () => setRateChoice((p) => ({ ...p, [o.id]: optKey(opt) }));
                                                  return (
                                                    <TableRow key={idx} hover={canPick} selected={isSel}
                                                      onClick={canPick ? pick : undefined}
                                                      sx={{ cursor: canPick ? 'pointer' : 'default', '& td': isSel ? { fontWeight: 600 } : undefined }}>
                                                      {canPick && (
                                                        <TableCell padding="checkbox">
                                                          <Radio size="small" checked={isSel} onChange={pick} />
                                                        </TableCell>
                                                      )}
                                                      <TableCell>
                                                        {opt.carrier || '—'}
                                                        {isBought && (
                                                          <Chip label={t('options.purchased')} size="small" color="primary" sx={{ ml: 1, height: 18 }} />
                                                        )}
                                                        {opt.selected && (!isBought || canPick) && (
                                                          <Chip label={t('options.cheapest')} size="small" color="success" variant="outlined" sx={{ ml: 1, height: 18 }} />
                                                        )}
                                                      </TableCell>
                                                      <TableCell>{opt.service || '—'}</TableCell>
                                                      <TableCell>
                                                        {opt.estimatedDaysMin != null
                                                          ? (opt.estimatedDaysMax && opt.estimatedDaysMax !== opt.estimatedDaysMin
                                                              ? `${opt.estimatedDaysMin}–${opt.estimatedDaysMax}`
                                                              : opt.estimatedDaysMin)
                                                          : '—'}
                                                      </TableCell>
                                                      <TableCell align="right">{fmtCurrency(opt.shippingCost)}</TableCell>
                                                    </TableRow>
                                                  );
                                                })}
                                              </TableBody>
                                            </Table>
                                          </TableContainer>
                                        </Box>
                                      );
                                    })()}
                                  </Grid>

                                  {/* Shipping & Tracking */}
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" fontWeight={700} mb={1}>{t('detail.shippingAddress')}</Typography>
                                    {o.shippingAddress ? (
                                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                                        {/* The address snapshot has never carried a name, so this
                                            line was always blank. The member's name is the
                                            recipient — it is what goes on the label. */}
                                        <Typography variant="body2" fontWeight={600}>
                                          {o.shippingAddress.name || o.shippingAddress.fullName
                                            || o.iboName || ''}
                                        </Typography>
                                        <Typography variant="body2">{o.shippingAddress.streetLine1}</Typography>
                                        {o.shippingAddress.streetLine2 && (
                                          <Typography variant="body2">{o.shippingAddress.streetLine2}</Typography>
                                        )}
                                        <Typography variant="body2">
                                          {o.shippingAddress.city}, {o.shippingAddress.state} {o.shippingAddress.zipCode}
                                        </Typography>
                                      </Paper>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary" mb={2}>{t('detail.notAvailable')}</Typography>
                                    )}

                                    <Typography variant="subtitle2" fontWeight={700} mb={1}>{t('detail.paymentInfo')}</Typography>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                                      <Grid container spacing={1}>
                                        <Grid item xs={12}>
                                          <Typography variant="caption" color="text.secondary">{t('detail.paymentId')}</Typography>
                                          <Box display="flex" alignItems="center" gap={0.5}>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                              {o.paymentId || '—'}
                                            </Typography>
                                            {o.paymentId && (
                                              <IconButton size="small" onClick={() => copyToClipboard(o.paymentId)}>
                                                <CopyIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                            )}
                                          </Box>
                                        </Grid>
                                      </Grid>
                                    </Paper>

                                    <Typography variant="subtitle2" fontWeight={700} mb={1}>{t('detail.shippingTracking')}</Typography>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                                      <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                          <Typography variant="caption" color="text.secondary">{t('detail.carrier')}</Typography>
                                          <Typography variant="body2">{o.carrier || '—'}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                          <Typography variant="caption" color="text.secondary">{t('detail.trackingNumber')}</Typography>
                                          <Box display="flex" alignItems="center" gap={0.5}>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                              {o.trackingNumber || '—'}
                                            </Typography>
                                            {o.trackingNumber && (
                                              <IconButton size="small" onClick={() => copyToClipboard(o.trackingNumber)}>
                                                <CopyIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                            )}
                                          </Box>
                                        </Grid>
                                        {o.shippedAt && (
                                          <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">{t('detail.shippedAt')}</Typography>
                                            <Typography variant="body2">{fmtDate(o.shippedAt)}</Typography>
                                          </Grid>
                                        )}
                                        {o.deliveredAt && (
                                          <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">{t('detail.deliveredAt')}</Typography>
                                            <Typography variant="body2">{fmtDate(o.deliveredAt)}</Typography>
                                          </Grid>
                                        )}
                                        {o.easypostShipmentId && (
                                          <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary">{t('detail.shipmentId')}</Typography>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                                {o.easypostShipmentId}
                                              </Typography>
                                              <IconButton size="small" onClick={() => copyToClipboard(o.easypostShipmentId)}>
                                                <CopyIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                            </Box>
                                          </Grid>
                                        )}
                                      </Grid>
                                    </Paper>

                                    {(o.labelUrl || o.labelTrackingUrl) && (
                                      <>
                                        <Typography variant="subtitle2" fontWeight={700} mb={1}>{t('detail.labelLinks')}</Typography>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                          <Box display="flex" flexDirection="column" gap={1}>
                                            {o.labelUrl && (
                                              <Button size="small" variant="outlined"
                                                disabled={printingIds.has(o.id)}
                                                startIcon={printingIds.has(o.id)
                                                  ? <CircularProgress size={14} /> : <PrintIcon />}
                                                onClick={() => printLabels([o.id])}
                                                sx={{ textTransform: 'none', justifyContent: 'flex-start' }}>
                                                {t('detail.printShippingLabel')}
                                              </Button>
                                            )}
                                            {o.labelTrackingUrl && (
                                              <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
                                                href={o.labelTrackingUrl} target="_blank" rel="noopener"
                                                sx={{ textTransform: 'none', justifyContent: 'flex-start' }}>
                                                {t('detail.trackPackage')}
                                              </Button>
                                            )}
                                          </Box>
                                        </Paper>
                                      </>
                                    )}

                                    {/* The buy button that used to sit here said the same thing as
                                        the row's, in different words. The shipment id stays — it is
                                        the reference someone needs when checking EasyPost. */}
                                    {o.easypostShipmentId && (
                                      <Box mt={2}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          {t('detail.shipment', { id: o.easypostShipmentId })}
                                        </Typography>
                                      </Box>
                                    )}

                                    {/* Refund Info */}
                                    {o.refundId && (
                                      <>
                                        <Typography variant="subtitle2" fontWeight={700} mb={1} mt={2}>{t('detail.refundDetails')}</Typography>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'error.main' }}>
                                          <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                              <Typography variant="caption" color="text.secondary">{t('detail.refundId')}</Typography>
                                              <Box display="flex" alignItems="center" gap={0.5}>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                  {o.refundId}
                                                </Typography>
                                                <IconButton size="small" onClick={() => copyToClipboard(o.refundId)}>
                                                  <CopyIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                              </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                              <Typography variant="caption" color="text.secondary">{t('detail.amountRefunded')}</Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                                                {fmtCurrency(o.refundAmount)}
                                              </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                              <Typography variant="caption" color="text.secondary">{t('detail.refundedAt')}</Typography>
                                              <Typography variant="body2">{fmtDate(o.refundedAt)}</Typography>
                                            </Grid>
                                            {o.refundReason && (
                                              <Grid item xs={6}>
                                                <Typography variant="caption" color="text.secondary">{t('detail.reason')}</Typography>
                                                <Typography variant="body2">{o.refundReason}</Typography>
                                              </Grid>
                                            )}
                                          </Grid>
                                        </Paper>
                                      </>
                                    )}

                                    {/* Refund button in detail section */}
                                    {canRefund(o) && (
                                      <Box mt={2}>
                                        <Button size="small" variant="outlined" color="error"
                                          onClick={() => openRefundDialog(o)}
                                          startIcon={<RefundIcon />}
                                          sx={{ textTransform: 'none' }}>
                                          {t('detail.processRefund')}
                                        </Button>
                                      </Box>
                                    )}
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div" count={totalElements}
              page={page} onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage} onRowsPerPageChange={handleRowsChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={t('pagination.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => (count !== -1
                ? t('pagination.displayed', { from, to, total: count })
                : t('pagination.displayedMore', { from, to }))}
              getItemAriaLabel={(type) => t(`pagination.${type}`)}
            />
          </>
        )}
      </TableContainer>

      {/* ─── Single Status Update Dialog ─── */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('statusDialog.title')}</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              <Trans t={t} i18nKey="statusDialog.order"
                values={{ order: statusTarget?.orderNumber || statusTarget?.id }}
                components={{ strong: <strong /> }} />
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('statusDialog.newStatus')}</InputLabel>
                  <Select value={newStatus} label={t('statusDialog.newStatus')}
                    onChange={(e) => setNewStatus(e.target.value)}>
                    {ORDER_STATUSES.map((s) => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label={t('statusDialog.trackingNumber')}
                  value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label={t('statusDialog.carrier')}
                  value={carrier} onChange={(e) => setCarrier(e.target.value)} />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setStatusDialog(false)} sx={{ textTransform: 'none' }}>{t('cancel')}</Button>
          <Button onClick={handleUpdateStatus} variant="contained" sx={{ textTransform: 'none', fontWeight: 600 }}>
            {t('statusDialog.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Bulk Label Purchase: confirm (DV-289) ─── */}
      <Dialog open={bulkLabelDialog} onClose={() => !bulkLabelLoading && setBulkLabelDialog(false)}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('bulkLabels.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <Trans t={t} i18nKey="bulkLabels.body" count={selectedNeedingLabel.length}
              values={{ count: selectedNeedingLabel.length }} components={{ strong: <strong /> }} />
          </Typography>
          {selectedAlreadyLabelled > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('bulkLabels.alreadyLabelled', { count: selectedAlreadyLabelled })}
            </Typography>
          )}
          {selectedNeedingLabel.length > MAX_BULK_LABELS && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {t('bulkLabels.tooMany', { max: MAX_BULK_LABELS })}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {t('bulkLabels.note')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkLabelDialog(false)} disabled={bulkLabelLoading}
            sx={{ textTransform: 'none' }}>{t('cancel')}</Button>
          <Button
            variant="contained" color="secondary"
            onClick={runBulkLabelPurchase}
            disabled={bulkLabelLoading
              || selectedNeedingLabel.length === 0
              || selectedNeedingLabel.length > MAX_BULK_LABELS}
            startIcon={bulkLabelLoading ? <CircularProgress size={16} /> : <PrintIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {bulkLabelLoading ? t('bulkLabels.buying') : t('bulkLabels.buy', { count: selectedNeedingLabel.length })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Bulk Label Purchase: per-order result (DV-289) ─── */}
      <Dialog open={!!bulkLabelResult} onClose={() => setBulkLabelResult(null)}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('bulkResult.title')}</DialogTitle>
        <DialogContent>
          {bulkLabelResult && (
            <>
              <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
                <Chip size="small" color="success" label={t('bulkResult.purchased', { count: bulkLabelResult.purchased })} />
                {bulkLabelResult.failed > 0 && (
                  <Chip size="small" color="error" label={t('bulkResult.failed', { count: bulkLabelResult.failed })} />
                )}
                {bulkLabelResult.notAttempted > 0 && (
                  <Chip size="small" label={t('bulkResult.notAttempted', { count: bulkLabelResult.notAttempted })} />
                )}
              </Box>
              {bulkLabelResult.haltedReason && (
                <Paper variant="outlined"
                  sx={{ p: 1.5, mb: 2, borderRadius: 2, borderColor: 'error.light' }}>
                  <Typography variant="body2" color="error">{translateServerMessage(bulkLabelResult.haltedReason)}</Typography>
                </Paper>
              )}
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 700 }}>{t('bulkResult.order')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('bulkResult.result')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('bulkResult.detail')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(bulkLabelResult.results || []).map((r) => (
                      <TableRow key={r.orderId}>
                        <TableCell sx={{ fontWeight: 600 }}>{r.orderNumber || r.orderId}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={r.status === 'PURCHASED' ? 'success' : r.status === 'FAILED' ? 'error' : 'default'}
                            label={bulkResultLabel(r.status)}
                          />
                        </TableCell>
                        <TableCell>
                          {r.status === 'PURCHASED' ? (
                            <Typography variant="caption">
                              {[r.carrier, r.service].filter(Boolean).join(' ')}
                              {r.cost != null && ` — ${fmtCurrency(r.cost)}`}
                              {r.trackingNumber && ` — ${r.trackingNumber}`}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {r.error ? translateServerMessage(r.error) : '—'}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setBulkLabelResult(null)}
            sx={{ textTransform: 'none', fontWeight: 600 }}>{t('bulkResult.done')}</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Bulk Status Update Dialog ─── */}
      <Dialog open={bulkDialog} onClose={() => setBulkDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('bulkStatus.title')}</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              <Trans t={t} i18nKey="bulkStatus.updating" count={selected.size}
                values={{ count: selected.size }} components={{ strong: <strong /> }} />
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('statusDialog.newStatus')}</InputLabel>
                  <Select value={bulkStatus} label={t('statusDialog.newStatus')}
                    onChange={(e) => setBulkStatus(e.target.value)}>
                    {ORDER_STATUSES.map((s) => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label={t('bulkStatus.trackingNumber')}
                  value={bulkTrackingNumber} onChange={(e) => setBulkTrackingNumber(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label={t('bulkStatus.carrier')}
                  value={bulkCarrier} onChange={(e) => setBulkCarrier(e.target.value)} />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setBulkDialog(false)} sx={{ textTransform: 'none' }}>{t('cancel')}</Button>
          <Button onClick={handleBulkUpdate} variant="contained" sx={{ textTransform: 'none', fontWeight: 600 }}>
            {t('bulkStatus.updateAll')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Refund Dialog ─── */}
      <Dialog open={refundDialog} onClose={() => !refundLoading && setRefundDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>{t('refund.title')}</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'error.50' }}>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                <Trans t={t} i18nKey="refund.order" values={{ order: refundTarget?.orderNumber }}
                  components={{ strong: <strong /> }} />
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                <Trans t={t} i18nKey="refund.paymentId" values={{ id: refundTarget?.paymentId }}
                  components={{ strong: <strong style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} /> }} />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Number(refundTarget?.refundAmount) > 0 ? (
                  <Trans t={t} i18nKey="refund.orderTotalWithRefunded"
                    values={{ total: fmtCurrency(refundTarget?.total), refunded: fmtCurrency(refundTarget?.refundAmount) }}
                    components={{ strong: <strong /> }} />
                ) : (
                  <Trans t={t} i18nKey="refund.orderTotal" values={{ total: fmtCurrency(refundTarget?.total) }}
                    components={{ strong: <strong /> }} />
                )}
              </Typography>
            </Paper>

            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>{t('refund.whatToRefund')}</Typography>
            <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              {REFUND_COMPONENTS.map((c) => {
                const amount = Number(c.amountOf(refundTarget) || 0);
                const done = refundedAlready.includes(c.key);
                const nothingToRefund = amount <= 0;
                return (
                  <Box key={c.key}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5,
                      borderBottom: '1px solid', borderColor: 'divider',
                      '&:last-of-type': { borderBottom: 'none' },
                    }}>
                    <Checkbox
                      size="small"
                      checked={refundParts.includes(c.key)}
                      disabled={done || nothingToRefund}
                      onChange={() => toggleRefundPart(c.key)}
                    />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}
                      color={done || nothingToRefund ? 'text.disabled' : 'text.primary'}>
                      {done
                        ? t('refund.componentRefunded', { component: t(`refund.components.${c.key}`) })
                        : t(`refund.components.${c.key}`)}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}
                      color={done || nothingToRefund ? 'text.disabled' : 'text.primary'}>
                      {fmtCurrency(amount)}
                    </Typography>
                  </Box>
                );
              })}
              <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, bgcolor: 'action.hover' }}>
                <Typography variant="body2" fontWeight={700} sx={{ flexGrow: 1 }}>{t('refund.total')}</Typography>
                <Typography variant="body1" fontWeight={700} color="error.main">
                  {fmtCurrency(refundTotal)}
                </Typography>
              </Box>
            </Paper>

            {booksWithoutTax && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {t('refund.booksWithoutTax')}
              </Typography>
            )}
            {taxOnly && (
              <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                {t('refund.taxOnly')}
              </Typography>
            )}
            <TextField
              fullWidth size="small" label={t('refund.reason')}
              multiline rows={2}
              value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
              placeholder={t('refund.reasonPlaceholder')}
            />
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              {t('refund.note')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setRefundDialog(false)} disabled={refundLoading}
            sx={{ textTransform: 'none' }}>
            {t('cancel')}
          </Button>
          <Button onClick={handleRefund} variant="contained" color="error"
            disabled={refundLoading || refundParts.length === 0 || refundTotal <= 0 || booksWithoutTax}
            startIcon={refundLoading ? <CircularProgress size={16} color="inherit" /> : <RefundIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            {refundLoading ? t('refund.processing') : t('refund.submit', { amount: fmtCurrency(refundTotal) })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Buy label — confirm the selected shipping option (DV-249) */}
      <Dialog open={!!labelDialog} onClose={() => labelLoading == null && setLabelDialog(null)}
        maxWidth="xs" fullWidth>
        <DialogTitle>
          {labelDialog
            ? t('labelDialog.titleWithOrder', { order: labelDialog.order.orderNumber })
            : t('labelDialog.title')}
        </DialogTitle>
        <DialogContent>
          {labelDialog && labelDialog.option ? (
            <Typography variant="body2" component="div">
              {t('labelDialog.purchasing')}
              <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                <strong>{labelDialog.option.carrier || '—'}</strong> {labelDialog.option.service || ''}
                {' — '}{fmtCurrency(labelDialog.option.shippingCost)}
                {labelDialog.option.estimatedDaysMin != null && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {labelDialog.option.estimatedDaysMax && labelDialog.option.estimatedDaysMax !== labelDialog.option.estimatedDaysMin
                      ? t('labelDialog.estDaysRange', {
                        min: labelDialog.option.estimatedDaysMin, max: labelDialog.option.estimatedDaysMax,
                      })
                      : t('labelDialog.estDays', { days: labelDialog.option.estimatedDaysMin })}
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                {t('labelDialog.fasterNote')}
              </Typography>
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('labelDialog.noOptions')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabelDialog(null)}
            disabled={labelDialog != null && labelLoading === labelDialog.order.id}>
            {t('cancel')}
          </Button>
          <Button variant="contained" color="secondary" onClick={confirmPurchaseLabel}
            disabled={labelDialog != null && labelLoading === labelDialog.order.id}
            startIcon={labelDialog != null && labelLoading === labelDialog.order.id
              ? <CircularProgress size={16} /> : <PrintIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            {t('labelDialog.buy')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookOrderManagementPage;
